import React, { useState, useEffect } from 'react';
import { KeyRound, X, Save, Lock, Mail, User, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { FirebaseAuthService } from '../../services/firebaseAuthService';
import { auth } from '../../lib/firebase';
import { updateProfile } from 'firebase/auth';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const AlterarMinhaSenhaModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      const userEmail = firebaseUser.email || '';
      setCurrentUserEmail(userEmail);
      setEmail(userEmail);
      setNome(firebaseUser.displayName || userEmail.split('@')[0] || '');
    }

    setErro(null);
    setSucesso(null);
    setNewPassword('');
    setConfirmPassword('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword && newPassword.length < 6) {
      setErro('A nova senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      setErro('A confirmação de senha não confere com a nova senha.');
      return;
    }

    setSalvando(true);
    setErro(null);
    setSucesso(null);

    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error('Nenhum usuário logado no Firebase.');
      }

      // Atualiza nome de exibição no Firebase Auth e Firestore se informado
      if (nome.trim() && nome.trim() !== firebaseUser.displayName) {
        await updateProfile(firebaseUser, { displayName: nome.trim() });
        await FirebaseAuthService.saveUserProfile(firebaseUser.uid, {
          displayName: nome.trim()
        });
      }

      // Atualiza senha se informada
      if (newPassword.trim()) {
        await FirebaseAuthService.updateCurrentUserPassword(newPassword.trim());
      }

      setSucesso('Credenciais atualizadas com sucesso no Firebase!');
      setTimeout(() => {
        onClose();
      }, 1400);
    } catch (err: any) {
      if (err?.code === 'auth/requires-recent-login') {
        setErro('Por segurança, faça login novamente antes de alterar a senha.');
      } else {
        setErro(err.message || 'Erro ao alterar credenciais no Firebase.');
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden text-left animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabeçalho */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-600/20 text-amber-400 flex items-center justify-center border border-amber-500/30">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white">Minhas Credenciais</h2>
              <p className="text-xs text-slate-400">
                Altere seu nome ou senha no Firebase
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {erro && (
            <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-300 text-xs font-bold">
              {erro}
            </div>
          )}

          {sucesso && (
            <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              {sucesso}
            </div>
          )}

          {/* Nome */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              Nome Completo
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Seu nome"
                className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
              />
            </div>
          </div>

          {/* E-mail (Somente leitura para identificação no Firebase) */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
              E-mail de Acesso
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                disabled
                value={email}
                className="w-full bg-slate-950/60 border border-slate-800 rounded-xl pl-9 pr-3 py-2 text-sm text-slate-400 cursor-not-allowed"
              />
            </div>
          </div>

          <div className="border-t border-slate-800 pt-3">
            <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Alterar Senha
            </h3>

            {/* Nova Senha */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  Nova Senha (deixe em branco para não alterar)
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-9 pr-10 py-2 text-sm text-white focus:outline-none focus:border-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirmar Nova Senha */}
              {newPassword.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">
                    Confirmar Nova Senha
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repita a nova senha"
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-red-600/30 transition disabled:opacity-50 cursor-pointer"
            >
              {salvando ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
